import { reactive } from 'vue'
import api from '@/utils/api'

/** What an EFT invoice is paying for. Clinch must still Approve after proof is sent. */
export function invoicePurpose(invoice) {
  if (!invoice) return 'EFT'
  const purpose = String(invoice.purpose || '').trim()
  if (purpose) return purpose
  const kind = String(invoice.kind || '').trim()
  if (kind === 'addon') return invoice.addon_label || 'Add-on'
  if (kind === 'credit_topup') return 'Credit wallet'
  if (kind === 'package') {
    return invoice.signup_intent_id ? 'Workspace signup' : 'Monthly subscription'
  }
  return kind ? kind.replace(/_/g, ' ') : 'EFT'
}

export function invoiceProofStatusLabel(invoice) {
  if (!invoice) return ''
  if (invoice.status === 'proof_received') return 'Awaiting approval'
  if (invoice.status === 'pending') return 'Waiting for proof'
  if (invoice.status === 'paid') return 'Approved'
  return invoice.status || ''
}

export function proofAwaitingLabel(invoice) {
  if (!invoice || invoice.status !== 'proof_received') return ''
  return `${invoicePurpose(invoice)} · awaiting approval`
}

/** Copy for the Clinch Approve modal when the full workspace row is not loaded. */
function formatProofAmount(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return 'the invoice amount'
  return `R ${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/**
 * Checks Clinch must tick before Approve writes anything.
 * A Credit wallet proof credits that platform's wallet only after every box is ticked.
 */
export function approvalConfirmations(invoice, platformName) {
  if (!invoice) return []
  const platform = platformName || invoice.tenant_name || 'this platform'
  const amount = formatProofAmount(invoice.amount_zar || invoice.amount)
  const reference = invoice.payment_reference || invoice.invoice_number || 'this invoice'
  const purpose = invoicePurpose(invoice)
  const kind = String(invoice.kind || '')
  let apply = `Record this payment on ${platform}.`
  if (kind === 'credit_topup') {
    apply = `Credit ${amount} to ${platform}'s Credit wallet.`
  } else if (kind === 'addon') {
    apply = `Apply this ${purpose} on ${platform} and update the monthly total.`
  } else if (invoice.signup_intent_id) {
    apply = `Activate ${platform} from this signup payment.`
  } else if (kind === 'package') {
    apply = `Record this monthly subscription payment for ${platform}.`
  }
  return [
    { id: 'reference', label: `The bank transfer matches reference ${reference}.` },
    { id: 'amount', label: `The amount received is ${amount}.` },
    { id: 'apply', label: apply },
  ]
}

export function invoiceApprovalSummary(invoice) {
  const purpose = invoicePurpose(invoice)
  const kind = String(invoice?.kind || '')
  if (kind === 'addon') {
    return `This ${purpose.toLowerCase()} is not on the workspace yet. Approve to apply it and update the monthly total.`
  }
  if (kind === 'credit_topup') {
    return 'This Credit wallet top-up is not applied yet. Approve to add the amount. Monthly subscription stays the same.'
  }
  if (invoice?.signup_intent_id) {
    return 'Approve this signup transfer. The workspace is created or activated after you approve.'
  }
  return 'Approve this monthly subscription transfer. The next billing date updates. Quoted monthly stays the same unless this invoice is an add-on.'
}

/** Shared viewer state. View proof stays on this page. */
export const eftProofViewer = reactive({
  invoiceId: null,
  filename: '',
  nonce: 0,
})

export function openEftProof(invoiceId, filename = '') {
  const id = Number(invoiceId)
  if (!id) return
  eftProofViewer.invoiceId = id
  eftProofViewer.filename = typeof filename === 'string' ? filename : ''
  eftProofViewer.nonce += 1
}

export function closeEftProof() {
  eftProofViewer.invoiceId = null
  eftProofViewer.filename = ''
}

/**
 * Load an EFT proof for the on-page viewer.
 * The file is streamed from Django (S3, then local media) with the admin session.
 * A raw S3 href is not used — older slips were stored as S3 URLs without an object.
 */
export async function loadEftProof(invoiceId) {
  const id = Number(invoiceId)
  if (!id) throw new Error('Could not open proof of payment.')
  let resp
  try {
    resp = await api.get(`/admin/tenants/eft-invoices/${id}/proof/?redirect=1`, {
      responseType: 'blob',
    })
  } catch (err) {
    let detail = err.response?.data?.detail || err.message || 'Could not open proof of payment.'
    const data = err.response?.data
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text())
        if (parsed?.detail) detail = String(parsed.detail)
      } catch {
        /* ignore */
      }
    }
    const wrapped = new Error(detail)
    wrapped.response = { data: { detail } }
    throw wrapped
  }
  const raw = resp.data
  const headerType = String(resp.headers?.['content-type'] || '')
  const blob = raw instanceof Blob ? raw : new Blob([raw], { type: headerType || 'application/octet-stream' })
  const contentType = blob.type || headerType || 'application/octet-stream'
  if (contentType.includes('json')) {
    let detail = 'Could not open proof of payment.'
    try {
      const parsed = JSON.parse(await blob.text())
      if (parsed?.detail) detail = String(parsed.detail)
    } catch {
      /* ignore */
    }
    const err = new Error(detail)
    err.response = { data: { detail }, status: 404 }
    throw err
  }
  return {
    url: URL.createObjectURL(blob),
    contentType,
  }
}
